// EduBridge Network — Students admin page.
// Lists every account with user_type=student, shows registration source
// (website / agent referral / web form / admin / csv import / other) with a
// filter dropdown so admins can drill in per channel. Powered by /api/students.
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { GraduationCap, Search, Users2, Globe2, Briefcase, FormInput, ShieldCheck, Upload, HelpCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_META = {
  website: { label: 'Website Registration', icon: Globe2, color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300' },
  agent_referral: { label: 'Agent Referral', icon: Briefcase, color: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300' },
  web_form: { label: 'Web Form', icon: FormInput, color: 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300' },
  admin: { label: 'Admin Added', icon: ShieldCheck, color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300' },
  csv_import: { label: 'CSV Import', icon: Upload, color: 'bg-slate-100 text-slate-800 dark:bg-slate-500/15 dark:text-slate-300' },
  other: { label: 'Other', icon: HelpCircle, color: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-200' },
};

const SourceBadge = ({ source }) => {
  const meta = SOURCE_META[source] || SOURCE_META.other;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${meta.color}`} data-testid={`student-source-${source}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};

const StudentsPage = () => {
  const { api } = useAuth();
  const [students, setStudents] = useState([]);
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source && source !== 'all') params.set('source', source);
      if (q.trim()) params.set('q', q.trim());
      const res = await api().get(`/students${params.toString() ? `?${params}` : ''}`);
      setStudents(res.data.students || []);
      setSources(res.data.sources || []);
    } catch {
      toast.error('Could not load students');
    } finally {
      setLoading(false);
    }
  };

  // Initial load & when source filter changes
  useEffect(() => { fetchStudents(); }, [source]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(fetchStudents, 300);
    return () => clearTimeout(t);
  }, [q]);

  // Live counts per source (from the full result set)
  const sourceCounts = useMemo(() => {
    const c = {};
    students.forEach((s) => {
      const key = s.registration_source || 'website';
      c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [students]);

  return (
    <div className="p-6 space-y-6" data-testid="students-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2A4F] dark:text-slate-100 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-[#F5A623]" /> Students
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every student account, with how they arrived at EduBridge Network.
          </p>
        </div>
        <Card className="min-w-[220px]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
              <Users2 className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total students</div>
              <div className="text-2xl font-bold text-[#1A2A4F] dark:text-slate-100" data-testid="students-total-count">{students.length}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-9"
            data-testid="students-search-input"
          />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[220px]" data-testid="students-source-filter">
            <SelectValue placeholder="Filter by source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="students-source-all">All sources</SelectItem>
            {sources.map((s) => (
              <SelectItem key={s.key} value={s.key} data-testid={`students-source-opt-${s.key}`}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchStudents} data-testid="students-refresh-btn">Refresh</Button>
      </div>

      {/* Source pills — quick summary */}
      <div className="flex flex-wrap gap-2">
        {sources.map((s) => (
          <button
            key={s.key}
            onClick={() => setSource(source === s.key ? 'all' : s.key)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition ${
              source === s.key ? 'bg-[#1A2A4F] text-white border-[#1A2A4F]' : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 hover:border-[#F5A623]'
            }`}
            data-testid={`students-source-chip-${s.key}`}
          >
            {s.label} <span className="opacity-70 ml-1">({sourceCounts[s.key] || 0})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground gap-2" data-testid="students-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading students…
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="students-empty">
              No students match this filter.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Country / City</TableHead>
                  <TableHead>Nationality</TableHead>
                  <TableHead>Registration Source</TableHead>
                  <TableHead className="text-center">Applications</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((s) => (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                    <TableCell>
                      <div className="font-medium text-[#1A2A4F] dark:text-slate-100">{s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || '—'}</div>
                      <div className="text-xs text-muted-foreground">{s.mobile || s.phone || ''}</div>
                    </TableCell>
                    <TableCell className="text-sm">{s.email}</TableCell>
                    <TableCell className="text-sm">
                      {(s.country || '—')}{s.city ? ` • ${s.city}` : ''}
                    </TableCell>
                    <TableCell className="text-sm">{s.nationality || '—'}</TableCell>
                    <TableCell>
                      <SourceBadge source={s.registration_source || 'website'} />
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" data-testid={`student-app-count-${s.id}`}>{s.application_count || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentsPage;
